import PropTypes from 'prop-types';
import { Checkbox, Inline, LinkButton, Stack, vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import sortBy from 'lodash/sortBy';
import Filter from '../Filter';

const actionsStyles = css`
  font-size: 0.875rem;
`;

const optionsWrapperStyles = css`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;

  @media ${mediaQueries.medium.up} {
    column-count: 3;
    column-gap: 30px;
  }

  @media ${mediaQueries.large.up} {
    column-count: 4;
  }
`;

const optionStyles = css`
  border-top: 1px solid ${vars.colors.divider};
  break-inside: avoid;
  font-size: 0.875rem;
  margin: 0;
  white-space: nowrap;

  & > * {
    display: inline-block;
    vertical-align: middle;
  }

  label {
    cursor: pointer;
    margin-left: 10px;
    padding: 12px 0;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: calc(100% - 1rem - 10px);

    html[dir='rtl'] & {
      margin-left: 0;
      margin-right: 10px;
    }
  }

  @media ${mediaQueries.large.up} {
    label {
      padding: 7px 0;
    }
  }
`;

const getOptionsCheckedValues = (selectableOptions, selectedOptionCodes) => {
  if (!selectedOptionCodes) {
    return Object.fromEntries(selectableOptions.map((option) => [option.code, true]));
  }

  if (selectedOptionCodes.length === 0) {
    return {};
  }

  return Object.fromEntries(selectedOptionCodes.map((code) => [code, true]));
};

const CheckboxFilter = ({
  className,
  isInitiallyExpanded,
  isToggleable,
  label,
  name,
  onFilter,
  options,
  selectedValues,
  shouldShowResetButton = true,
}) => {
  const { t } = useTranslation();
  const checkedOptionValues = getOptionsCheckedValues(options, selectedValues);
  const sortedOptions = sortBy(options, ['name']);

  const handleChange = (code) => () => {
    checkedOptionValues[code] = !checkedOptionValues[code];

    const selectedOptions = Object.entries(checkedOptionValues)
      .filter((entry) => entry[1])
      .map((entry) => entry[0]);

    if (selectedOptions.length === sortedOptions.length) {
      onFilter({ [name]: null });
    } else {
      onFilter({ [name]: selectedOptions });
    }
  };

  const handleClearAll = () => {
    onFilter({ [name]: [] });
  };

  const handleSelectAll = () => {
    onFilter({ [name]: null });
  };

  return (
    <Filter
      className={className}
      isInitiallyExpanded={isInitiallyExpanded}
      isSelected={Boolean(selectedValues)}
      isToggleable={isToggleable}
      label={label}
      name={name}
      onReset={handleSelectAll}
      shouldShowResetButton={shouldShowResetButton}
    >
      <Stack>
        <Inline spacing={8}>
          <LinkButton className={actionsStyles} onClick={handleClearAll}>
            {t('Result.SearchFilters.ClearAll.Button')}
          </LinkButton>
          <LinkButton className={actionsStyles} onClick={handleSelectAll}>
            {t('Result.SearchFilters.SelectAll.Button')}
          </LinkButton>
        </Inline>

        <ul className={optionsWrapperStyles}>
          {sortedOptions.map((option) => (
            <li className={optionStyles} key={`${name.toLowerCase()}-${option.code}`}>
              <Checkbox
                checked={Boolean(checkedOptionValues[option.code])}
                id={`${name.toLowerCase()}-${option.code}`}
                onChange={handleChange(option.code)}
                value={option.code}
              />
              <label htmlFor={`${name.toLowerCase()}-${option.code}`} title={option.name}>
                {option.name}
              </label>
            </li>
          ))}
        </ul>
      </Stack>
    </Filter>
  );
};

CheckboxFilter.propTypes = {
  className: PropTypes.string,
  isInitiallyExpanded: PropTypes.bool,
  isToggleable: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string,
  onFilter: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  selectedValues: PropTypes.arrayOf(PropTypes.string),
  shouldShowResetButton: PropTypes.bool,
};

export default CheckboxFilter;
